/**
 * Request deduplication and caching utility
 *
 * Prevents duplicate API calls by:
 * 1. Returning in-flight promises for concurrent requests
 * 2. Caching results for a TTL period
 * 3. Managing request lifecycle
 */

import { createLogger } from './logger';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

const log = createLogger('requestCache');

class RequestCache {
  // In-flight requests (prevents duplicates)
  private pending = new Map<string, Promise<any>>();

  // Cached results
  private cache = new Map<string, CacheEntry<any>>();

  /**
   * Get data with deduplication and caching
   *
   * @param key - Unique cache key
   * @param fetcher - Function to fetch data if not cached
   * @param ttl - Time to live in milliseconds (default: 5 minutes)
   */
  async get<T>(
    key: string,
    fetcher: () => Promise<T>,
    ttl: number = 5 * 60 * 1000
  ): Promise<T> {
    const cached = this.cache.get(key);
    if (cached) {
      const age = Date.now() - cached.timestamp;
      if (age < cached.ttl) {
        log.debug('Cache hit', { key, ageSeconds: Math.round(age / 1000) });
        return cached.data as T;
      } else {
        log.debug('Cache expired', { key });
        this.cache.delete(key);
      }
    }

    if (this.pending.has(key)) {
      log.debug('Request deduplicated', { key });
      return this.pending.get(key) as Promise<T>;
    }

    log.debug('Cache miss', { key });
    const promise = fetcher();
    this.pending.set(key, promise);

    try {
      const data = await promise;

      // Cache the result
      this.cache.set(key, {
        data,
        timestamp: Date.now(),
        ttl,
      });

      log.debug('Cached new entry', { key, ttlSeconds: ttl / 1000 });
      return data;
    } catch (error) {
      log.error(`Request failed for ${key}`, error);
      throw error;
    } finally {
      this.pending.delete(key);
    }
  }

  /**
   * Invalidate a specific cache entry
   */
  invalidate(key: string): void {
    if (this.cache.delete(key)) {
      log.debug('Invalidated cache entry', { key });
    }
  }

  /**
   * Invalidate all cache entries matching a pattern
   */
  invalidatePattern(pattern: RegExp): void {
    const keys = Array.from(this.cache.keys());
    const matchedKeys = keys.filter(key => pattern.test(key));
    matchedKeys.forEach(key => this.cache.delete(key));
    if (matchedKeys.length > 0) {
      log.info('Invalidated cache entries by pattern', {
        count: matchedKeys.length,
        pattern: pattern.toString(),
      });
    }
  }

  /**
   * Clear all caches
   */
  clear(): void {
    this.cache.clear();
    this.pending.clear();
    log.info('Cleared all request cache entries');
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      cacheSize: this.cache.size,
      pendingRequests: this.pending.size,
      entries: Array.from(this.cache.keys()),
    };
  }

  /**
   * Prune expired entries
   */
  prune(): void {
    const now = Date.now();
    let pruned = 0;

    const entries = Array.from(this.cache.entries());

    entries.forEach(([key, entry]) => {
      if (now - entry.timestamp >= entry.ttl) {
        this.cache.delete(key);
        pruned++;
      }
    });

    if (pruned > 0) {
      log.info('Pruned expired request cache entries', { count: pruned });
    }
  }
}

export const requestCache = new RequestCache();

setInterval(() => {
  requestCache.prune();
}, 5 * 60 * 1000);

export const createCacheKey = (...parts: (string | number | boolean | null | undefined)[]): string => {
  return parts.filter(p => p !== null && p !== undefined).join(':');
};

// Export class for testing
export { RequestCache };
